import React, { useEffect, useState, useRef } from 'react';
import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion';
import { AnnouncementBar } from '../components/AnnouncementBar';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Vial } from '../components/Vial';
/* ============================================================
 * V1 — Realistic float with dynamic shadow
 * Vial floats up, shadow inversely scales smaller + lighter.
 * Vial floats back down, shadow widens + darkens. Physics-aware.
 * ============================================================ */
function V1RealisticFloat() {
  return <Showcase title="Realistic float" tag="With light-aware shadow" data-id="element-1808">
      <div className="relative h-[360px] w-full flex items-end justify-center pb-6" data-id="element-1809">
        {/* Ground shadow — scales inversely to vial Y position */}
        <motion.div aria-hidden animate={{
        scaleX: [1, 0.7, 1],
        scaleY: [1, 0.6, 1],
        opacity: [0.18, 0.07, 0.18]
      }} transition={{
        duration: 4,
        repeat: Infinity,
        ease: 'easeInOut'
      }} className="absolute bottom-4 h-3 w-[180px] rounded-full bg-foreground origin-center" style={{
        filter: 'blur(8px)'
      }} data-id="element-1810" />

        {/* Vial — floats up + down, shadow disabled (we handle it separately) */}
        <motion.div animate={{
        y: [0, -22, 0]
      }} transition={{
        duration: 4,
        repeat: Infinity,
        ease: 'easeInOut'
      }} className="relative text-foreground" data-id="element-1811">
          <Vial label="BPC-157" size={240} showShadow={false} data-id="element-1812" />
        </motion.div>
      </div>
      <Caption data-id="element-1813">
        Vial translateY paired with a separately-animated shadow that scales and
        lightens inversely. Same duration + easing so they stay locked. Shadow
        blurred via filter for soft falloff.
      </Caption>
    </Showcase>;
}
/* ============================================================
 * V2 — Verified pulse
 * Vial stays still. Concentric primary-green rings pulse outward
 * from a "verified" point on the label every few seconds. Signal
 * of trust without movement.
 * ============================================================ */
function V2VerifiedPulse() {
  return <Showcase title="Verified pulse" tag="Trust signal, no movement" data-id="element-1814">
      <div className="relative h-[360px] w-full flex items-end justify-center pb-6" data-id="element-1815">
        <div className="relative text-foreground" data-id="element-1816">
          <Vial label="BPC-157" size={240} data-id="element-1817" />

          {/* Verified indicator + pulsing rings — positioned over the label */}
          <div className="absolute top-[58%] right-[14%] z-10" data-id="element-1818">
            <span className="relative inline-flex" data-id="element-1819">
              {/* Pulse rings — 3 staggered */}
              {[0, 0.8, 1.6].map((delay, i) => <motion.span key={i} aria-hidden className="absolute inset-0 rounded-full bg-primary" initial={{
              scale: 0.6,
              opacity: 0.6
            }} animate={{
              scale: 3,
              opacity: 0
            }} transition={{
              duration: 2.4,
              repeat: Infinity,
              delay,
              ease: 'easeOut'
            }} style={{
              width: 14,
              height: 14
            }} data-id="element-1820" />)}
              {/* Solid core dot */}
              <span className="relative inline-block h-[14px] w-[14px] rounded-full bg-primary border-2 border-background" data-id="element-1821" />
            </span>
          </div>
        </div>
      </div>
      <Caption data-id="element-1822">
        Stationary vial with a verified-check pulse ring overlay. Three rings
        stagger every 800ms, each scales 0.6→3 over 2.4s while fading.
        Communicates "live, monitored, verified."
      </Caption>
    </Showcase>;
}
/* ============================================================
 * V3 — Cold-chain mist
 * Small white particles rise from the cap and fade out — references
 * cold-chain shipping vapour. Vial does a very slow gentle float.
 * ============================================================ */
function V3ColdChainMist() {
  const particles = [{
    left: '38%',
    delay: 0,
    dur: 3.2
  }, {
    left: '50%',
    delay: 0.7,
    dur: 2.8
  }, {
    left: '60%',
    delay: 1.4,
    dur: 3.5
  }, {
    left: '44%',
    delay: 2.0,
    dur: 3.0
  }, {
    left: '56%',
    delay: 2.6,
    dur: 3.3
  }];
  return <Showcase title="Cold-chain mist" tag="Atmospheric vapour" data-id="element-1823">
      <div className="relative h-[360px] w-full flex items-end justify-center pb-6 overflow-hidden" data-id="element-1824">
        {/* Mist particles */}
        {particles.map((p, i) => <motion.span key={i} aria-hidden className="absolute bottom-[58%] h-2 w-2 rounded-full bg-foreground" style={{
        left: p.left,
        filter: 'blur(3px)'
      }} initial={{
        y: 0,
        opacity: 0,
        scale: 0.6
      }} animate={{
        y: -100,
        opacity: [0, 0.18, 0],
        scale: [0.6, 1.4, 1.8]
      }} transition={{
        duration: p.dur,
        repeat: Infinity,
        delay: p.delay,
        ease: 'easeOut'
      }} data-id="element-1825" />)}

        {/* Gentle float */}
        <motion.div animate={{
        y: [0, -6, 0]
      }} transition={{
        duration: 6,
        repeat: Infinity,
        ease: 'easeInOut'
      }} className="relative text-foreground" data-id="element-1826">
          <Vial label="BPC-157" size={240} data-id="element-1827" />
        </motion.div>
      </div>
      <Caption data-id="element-1828">
        Five blurred dot particles rise from above the cap with staggered delays
        and durations (2.8–3.5s) so they appear randomized. Suggests
        refrigerated vapour without literal fog.
      </Caption>
    </Showcase>;
}
/* ============================================================
 * V4 — Three-vial parade
 * Three vials in a row, each with its own staggered float +
 * independently-scaling shadow. Reads as inventory / scale of
 * operation, not a single product.
 * ============================================================ */
function V4ThreeVialParade() {
  const vials = [{
    label: 'BPC-157',
    delay: 0
  }, {
    label: 'TB-500',
    delay: 0.4
  }, {
    label: 'GHK-Cu',
    delay: 0.8
  }];
  return <Showcase title="Three-vial parade" tag="Staggered phase" data-id="element-1829">
      <div className="relative h-[360px] w-full flex items-end justify-center gap-10 pb-6" data-id="element-1830">
        {vials.map((v, i) => <div key={i} className="relative flex flex-col items-center justify-end" data-id="element-1831">
            {/* Floating vial */}
            <motion.div animate={{
          y: [0, -18, 0]
        }} transition={{
          duration: 4,
          repeat: Infinity,
          ease: 'easeInOut',
          delay: v.delay
        }} className="relative text-foreground" data-id="element-1832">
              <Vial label={v.label} size={170} showShadow={false} data-id="element-1833" />
            </motion.div>
            {/* Per-vial dynamic shadow */}
            <motion.div aria-hidden animate={{
          scaleX: [1, 0.72, 1],
          scaleY: [1, 0.6, 1],
          opacity: [0.16, 0.06, 0.16]
        }} transition={{
          duration: 4,
          repeat: Infinity,
          ease: 'easeInOut',
          delay: v.delay
        }} className="h-2.5 w-[120px] rounded-full bg-foreground origin-center -mt-1" style={{
          filter: 'blur(6px)'
        }} data-id="element-1834" />
          </div>)}
      </div>
      <Caption data-id="element-1835">
        Three vials with phase-offset float (0s, 0.4s, 0.8s delays) and their
        own light-aware shadows. The visual rhythm reads as a small production
        line.
      </Caption>
    </Showcase>;
}
/* ============================================================
 * V5 — Magnetic hover tilt
 * Vial idles still. On mouse-over, gently tilts toward the cursor
 * with spring physics — like a real object being attracted to your
 * pointer. Springs back on leave.
 * ============================================================ */
function V5MagneticTilt() {
  const containerRef = useRef<HTMLDivElement>(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const rotateX = useSpring(useTransform(y, [-100, 100], [10, -10]), {
    stiffness: 180,
    damping: 16
  });
  const rotateY = useSpring(useTransform(x, [-100, 100], [-10, 10]), {
    stiffness: 180,
    damping: 16
  });
  const handleMove = (e: React.MouseEvent) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    x.set(e.clientX - rect.left - rect.width / 2);
    y.set(e.clientY - rect.top - rect.height / 2);
  };
  const handleLeave = () => {
    x.set(0);
    y.set(0);
  };
  return <Showcase title="Magnetic tilt" tag="Interactive hover" data-id="element-1836">
      <div ref={containerRef} onMouseMove={handleMove} onMouseLeave={handleLeave} className="relative h-[360px] w-full flex items-end justify-center pb-6" style={{
      perspective: 700
    }} data-id="element-1837">
        <motion.div style={{
        rotateX,
        rotateY,
        transformStyle: 'preserve-3d'
      }} className="relative text-foreground" data-id="element-1838">
          <Vial label="BPC-157" size={240} data-id="element-1839" />
        </motion.div>
      </div>
      <Caption data-id="element-1840">
        Cursor-tracking spring tilt — ±10° on both axes, damped spring return
        (stiffness 180, damping 16). 700px perspective gives a subtle 3D effect.
        Move your mouse around the vial to feel it.
      </Caption>
    </Showcase>;
}
/* ============================================================
 * V6 — Sequential reveal
 * The vial parts appear one at a time on loop — body, then cap, then
 * label, then the verified check stamps. Shows the product being
 * "assembled / certified" repeatedly.
 * ============================================================ */
function V6SequentialReveal() {
  const [tick, setTick] = useState(0);
  // Restart the sequence every 5s
  useEffect(() => {
    const t = setInterval(() => setTick(n => n + 1), 5500);
    return () => clearInterval(t);
  }, []);
  return <Showcase title="Sequential reveal" tag="Build-up loop" data-id="element-1841">
      <div className="relative h-[360px] w-full flex items-end justify-center pb-6" data-id="element-1842">
        {/* Vial fades in as base */}
        <motion.div key={tick} initial={{
        opacity: 0,
        y: 14
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.55,
        ease: [0.22, 1, 0.36, 1]
      }} className="relative text-foreground" data-id="element-1843">
          <Vial label="BPC-157" size={240} data-id="element-1844" />

          {/* Annotations appear after the vial */}
          <motion.div key={`p-${tick}`} initial={{
          opacity: 0,
          x: 14
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.45,
          delay: 1.0
        }} className="absolute top-2 right-[-110px] rounded-md bg-background border border-border px-2.5 py-1.5 shadow-sm" data-id="element-1845">
            <div className="text-[8px] uppercase tracking-wider text-muted-foreground font-medium" data-id="element-1846">
              Purity
            </div>
            <div className="font-mono text-[12px] text-foreground font-medium leading-none mt-0.5" data-id="element-1847">
              99.7%
            </div>
          </motion.div>

          {/* Verified check stamps in last */}
          <motion.div key={`v-${tick}`} initial={{
          opacity: 0,
          scale: 0.4,
          rotate: -8
        }} animate={{
          opacity: 1,
          scale: 1,
          rotate: 0
        }} transition={{
          duration: 0.5,
          delay: 2.0,
          ease: [0.22, 1, 0.36, 1]
        }} className="absolute -bottom-2 -left-6 h-12 w-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow-md" style={{
          transform: 'rotate(-12deg)'
        }} data-id="element-1848">
            <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden data-id="element-1849">
              <path d="M 6 12 L 10 16 L 18 8" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" data-id="element-1850" />
            </svg>
          </motion.div>
        </motion.div>
      </div>
      <Caption data-id="element-1851">
        Vial fades up, then the purity annotation slides in (1s delay), then the
        primary-green verified stamp punches in with a scale+rotate impact (2s
        delay). Loops every 5.5 seconds.
      </Caption>
    </Showcase>;
}
/* ============================================================
 * Shared chrome
 * ============================================================ */
function Showcase({
  title,
  tag,
  children
}: {
  title: string;
  tag: string;
  children: React.ReactNode;
}) {
  return <article className="rounded-2xl border border-border bg-card overflow-hidden" data-id="element-1852">
      <div className="flex items-end justify-between gap-4 px-6 pt-6 pb-4 border-b border-border" data-id="element-1853">
        <div data-id="element-1854">
          <h2 className="text-[17px] font-semibold tracking-tight text-foreground leading-tight" data-id="element-1855">
            {title}
          </h2>
          <p className="mt-0.5 font-mono text-[10.5px] uppercase tracking-wider text-muted-foreground" data-id="element-1856">
            {tag}
          </p>
        </div>
      </div>
      <div className="bg-secondary/30" data-id="element-1857">{children}</div>
    </article>;
}
function Caption({
  children
}: {
  children: React.ReactNode;
}) {
  return <div className="px-6 py-4 border-t border-border text-[12.5px] text-muted-foreground leading-relaxed" data-id="element-1858">
      {children}
    </div>;
}
/* ============================================================
 * Page
 * ============================================================ */
export function VialLab() {
  return <div className="min-h-screen w-full bg-background" data-id="element-1859">
      <AnnouncementBar data-id="element-1860" />
      <Header data-id="element-1861" />
      <main className="container mx-auto py-16 lg:py-20" data-id="element-1862">
        <div className="max-w-2xl mb-12" data-id="element-1863">
          <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium mb-3" data-id="element-1864">
            Internal · Motion
          </p>
          <h1 className="text-3xl lg:text-5xl font-semibold tracking-[-0.025em] text-foreground leading-[1.05]" data-id="element-1865">
            Vial animation lab<span className="text-primary" data-id="element-1866">.</span>
          </h1>
          <p className="mt-5 text-[15px] leading-relaxed text-muted-foreground" data-id="element-1867">
            Six creative directions for the hero vial. Each is self-contained
            and isolated — no two motion contexts share a transform owner. Pick
            a direction and we&apos;ll pull it into the Hero.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-5" data-id="element-1868">
          <V1RealisticFloat data-id="element-1869" />
          <V2VerifiedPulse data-id="element-1870" />
          <V3ColdChainMist data-id="element-1871" />
          <V4ThreeVialParade data-id="element-1872" />
          <V5MagneticTilt data-id="element-1873" />
          <V6SequentialReveal data-id="element-1874" />
        </div>
      </main>
      <Footer data-id="element-1875" />
    </div>;
}